export const TERMS_AND_CONDITIONS = `# Términos y Condiciones de Uso — Kunveno (Abako)

**Última actualización:** 9 de Junio de 2026
**Versión:** 1.0

---

## Aviso importante

Estos Términos y Condiciones constituyen un acuerdo legalmente vinculante entre el usuario y el titular de la plataforma. Te recomendamos leerlos íntegramente antes de utilizar el servicio. El uso de la plataforma implica la aceptación plena de estos términos.

---

## 1. Información general e identificación del titular

La presente plataforma **Abako Work3places** (en adelante, también «Abako», la «Plataforma», la «Aplicación» o el «Servicio») es titularidad de:

- **Denominación social:** Abako Work3places S.L.
- **NIF/CIF:** B-12345678
- **Domicilio:** Calle de la Tecnología 42, Madrid, España
- **Correo de contacto:** legal@abako.xyz

(en adelante, el «Titular» o «nosotros»).

## 2. Objeto

Estos Términos y Condiciones (en adelante, los «Términos») regulan el acceso, registro y uso de la Plataforma, así como de todas las funcionalidades, contenidos y servicios disponibles a través de ella.

Kunveno es una plataforma de **gestión de proyectos basada en tecnología blockchain**, construida sobre la red Polkadot/Substrate, que permite a sus usuarios crear, organizar y coordinar proyectos y equipos de trabajo, registrando determinada información de forma descentralizada e inmutable mediante contratos inteligentes (*smart contracts*).

## 3. Definiciones

A efectos de los presentes Términos, se entenderá por:

- **Usuario:** toda persona física o jurídica que accede, se registra o utiliza la Plataforma.
- **Wallet (cartera):** monedero digital (p. ej. Talisman u otros compatibles) que el Usuario utiliza para autenticarse e interactuar con la Plataforma.
- **Clave privada / frase semilla:** credenciales criptográficas bajo el control exclusivo del Usuario que dan acceso a su Wallet.
- **Smart contract (contrato inteligente):** programa autoejecutable desplegado en la red blockchain que ejecuta determinadas funciones de la Plataforma de forma automática.
- **On-chain:** información o transacciones registradas directamente en la red blockchain.
- **Off-chain:** información almacenada fuera de la red blockchain (p. ej. en servidores del Titular).
- **Red:** la red blockchain Polkadot/Substrate (o las parachains/redes asociadas) sobre la que opera la Plataforma.
- **Transacción:** cualquier operación registrada en la Red iniciada por el Usuario a través de la Plataforma.

## 4. Requisitos de acceso y capacidad

Para utilizar la Plataforma, el Usuario declara y garantiza que:

1. Es mayor de **18 años** o de la edad mínima legal exigida en su jurisdicción.
2. Tiene plena capacidad jurídica y de obrar para aceptar estos Términos.
3. En caso de actuar en nombre de una persona jurídica, dispone de poderes suficientes para vincularla.
4. No tiene prohibido el uso del Servicio en virtud de la legislación aplicable.

El Titular podrá restringir el acceso desde jurisdicciones en las que el Servicio no esté disponible o sea contrario a la normativa local.

## 5. Registro, cuenta y autenticación mediante Wallet

5.1. El acceso a determinadas funcionalidades requiere la **conexión de una Wallet compatible** (p. ej. Talisman). La autenticación se realiza mediante firma criptográfica, sin que el Titular almacene ni tenga acceso a las claves privadas del Usuario.

5.2. **El Usuario es el único responsable de la custodia y seguridad de su Wallet**, claves privadas y frases semilla. El Titular **no puede recuperar, restablecer ni reemplazar** dichas credenciales en caso de pérdida, robo o acceso no autorizado.

5.3. El Usuario se compromete a notificar de inmediato al Titular cualquier uso no autorizado de su cuenta o Wallet del que tenga conocimiento.

5.4. El Usuario es responsable de toda actividad realizada a través de su Wallet conectada a la Plataforma.

## 6. Descripción y alcance del Servicio

6.1. La Plataforma ofrece herramientas para la creación y gestión de proyectos, asignación de tareas, coordinación de equipos y registro de determinada información de manera descentralizada.

6.2. Parte de la información y de las operaciones se registran **on-chain** y, por tanto, quedan sujetas a las características técnicas de la Red (ver Cláusula 7).

6.3. El Titular podrá añadir, modificar o suprimir funcionalidades del Servicio en cualquier momento, procurando comunicar los cambios sustanciales con antelación razonable.

## 7. Naturaleza blockchain: inmutabilidad, transacciones y riesgos

El Usuario reconoce y acepta expresamente las siguientes características inherentes a la tecnología blockchain:

7.1. **Inmutabilidad e irreversibilidad.** La información registrada on-chain y las transacciones confirmadas en la Red **no pueden modificarse, revertirse ni eliminarse**. El Usuario debe verificar cuidadosamente toda operación antes de confirmarla.

7.2. **Comisiones de red (*gas fees*).** Determinadas operaciones pueden conllevar comisiones de red exigidas por la propia blockchain, ajenas al control del Titular y no reembolsables.

7.3. **Dependencia de terceros.** El funcionamiento del Servicio depende de infraestructura de terceros (red Polkadot/Substrate, nodos, validadores, proveedores de Wallet). El Titular no responde de fallos, congestión, *forks*, interrupciones o cambios en dichas infraestructuras.

7.4. **Riesgos tecnológicos.** El uso de tecnología blockchain y *smart contracts* conlleva riesgos inherentes, incluyendo posibles vulnerabilidades, errores de código, ataques o evoluciones regulatorias. El Usuario asume dichos riesgos de forma informada.

7.5. **Ausencia de custodia.** El Titular **no custodia activos digitales, fondos ni claves** del Usuario.

## 8. Obligaciones del Usuario y uso aceptable

El Usuario se compromete a:

1. Usar la Plataforma conforme a la ley, la buena fe y estos Términos.
2. No utilizar el Servicio para fines ilícitos, fraudulentos o lesivos de derechos de terceros.
3. No introducir contenido ilegal, difamatorio, que infrinja derechos de propiedad intelectual o que vulnere la normativa de protección de datos.
4. No realizar ingeniería inversa, alterar, interferir o sobrecargar la Plataforma ni intentar acceder de forma no autorizada a sus sistemas o a los de la Red.
5. No emplear bots, *scraping* u otros medios automatizados no autorizados.
6. No suplantar la identidad de terceros ni utilizar Wallets de terceros sin autorización.

El incumplimiento de estas obligaciones podrá conllevar la suspensión o cancelación del acceso, sin perjuicio de las acciones legales que correspondan.

## 9. Contenido y datos del Usuario

9.1. El Usuario conserva la titularidad sobre los contenidos y datos de proyecto que aporta a la Plataforma («Contenido del Usuario»).

9.2. El Usuario concede al Titular una licencia no exclusiva, mundial y gratuita para alojar, reproducir y tratar dicho Contenido **en la medida estrictamente necesaria** para prestar el Servicio.

9.3. El Usuario es responsable de la licitud del Contenido que aporta y garantiza disponer de los derechos necesarios sobre el mismo.

9.4. El Usuario es consciente de que el Contenido o los metadatos registrados **on-chain** quedarán sujetos a la inmutabilidad descrita en la Cláusula 7.


## 10. Propiedad intelectual e industrial

10.1. Todos los derechos sobre la Plataforma —incluyendo software, código, diseño, marcas, logotipos y contenidos propios— pertenecen al Titular o a sus licenciantes y están protegidos por la legislación aplicable.

10.2. El Titular concede al Usuario una licencia **limitada, personal, revocable, no exclusiva e intransferible** para utilizar la Plataforma conforme a estos Términos. No se cede ningún otro derecho.

10.3. Queda prohibida la reproducción, distribución, modificación o explotación de la Plataforma o de cualquiera de sus elementos sin autorización expresa del Titular.

## 11. Protección de datos personales

12.1. El tratamiento de los datos personales del Usuario se rige por la **Política de Privacidad** disponible en la plataforma, que forma parte integrante de estos Términos.

12.2. El tratamiento se realiza conforme al Reglamento (UE) 2016/679 (RGPD) y a la Ley Orgánica 3/2018 (LOPDGDD).

12.3. **Tensión entre blockchain y derecho de supresión.** El Usuario reconoce que la información registrada on-chain es, por su naturaleza técnica, inmutable, lo que puede limitar el ejercicio efectivo del derecho de supresión sobre dichos datos. El Titular adopta medidas para minimizar los datos personales registrados on-chain y aplicar, cuando proceda, técnicas como la disociación, el *hashing* o el cifrado para mitigar este efecto.

## 12. Disponibilidad, modificaciones y suspensión del Servicio

12.1. El Titular procurará mantener la Plataforma disponible, pero **no garantiza** su funcionamiento ininterrumpido, dado que depende de factores técnicos y de terceros (Cláusula 7).

12.2. El Titular podrá suspender total o parcialmente el Servicio por motivos de mantenimiento, seguridad, actualizaciones o causas de fuerza mayor.

12.3. El Titular no será responsable de las indisponibilidades imputables a la Red, a los proveedores de Wallet o a otras infraestructuras de terceros.

## 13. Exención de garantías

El Servicio se presta **«tal cual» y «según disponibilidad»** («*as is*» / «*as available*»). En la máxima medida permitida por la ley, el Titular no otorga garantías de comerciabilidad, idoneidad para un fin concreto, exactitud, seguridad o ausencia de errores. Nada en esta cláusula limita los derechos que la normativa de consumidores reconozca a los Usuarios que tengan tal condición.

## 14. Limitación de responsabilidad

14.1. En la máxima medida permitida por la ley, el Titular no será responsable de daños indirectos, lucro cesante, pérdida de datos, pérdida de activos digitales o daños derivados de:

- el uso o la imposibilidad de uso de la Plataforma;
- transacciones on-chain irreversibles confirmadas por el Usuario;
- la pérdida, robo o uso indebido de las claves o Wallet del Usuario;
- fallos, ataques o interrupciones de la Red o de terceros proveedores.

14.2. Las limitaciones de esta cláusula no excluyen la responsabilidad que no pueda excluirse legalmente, incluyendo dolo, culpa grave o los derechos reconocidos a consumidores.

## 15. Indemnización

El Usuario se compromete a mantener indemne al Titular frente a reclamaciones, daños o gastos (incluidos honorarios razonables de defensa) derivados del incumplimiento de estos Términos, del uso indebido de la Plataforma o de la infracción de derechos de terceros o de la normativa aplicable.

## 16. Duración y terminación

16.1. Estos Términos permanecen vigentes mientras el Usuario utilice la Plataforma.

16.2. El Usuario puede dejar de utilizar el Servicio en cualquier momento desconectando su Wallet y cesando en su uso.

16.3. El Titular podrá suspender o cancelar el acceso del Usuario en caso de incumplimiento de estos Términos o por exigencia legal. La información ya registrada on-chain permanecerá conforme a la naturaleza inmutable de la Red.

## 17. Modificación de los Términos

El Titular podrá modificar estos Términos para adaptarlos a cambios legales, técnicos o del Servicio. Los cambios sustanciales se comunicarán a través de la Plataforma o por los medios de contacto facilitados. El uso continuado tras la entrada en vigor de las modificaciones implica su aceptación.

## 18. Ley aplicable y jurisdicción

18.1. Estos Términos se rigen por la legislación **española**.

18.2. Para la resolución de cualquier controversia, las partes se someten a los Juzgados y Tribunales de Madrid, salvo que la normativa de consumidores establezca un fuero imperativo distinto a favor del Usuario consumidor.

## 19. Disposiciones generales

19.1. **Nulidad parcial:** si alguna cláusula fuese declarada nula, el resto de los Términos seguirá siendo válido y aplicable.

19.2. **Cesión:** el Usuario no podrá ceder su posición contractual sin consentimiento del Titular. El Titular podrá cederla a entidades de su grupo o en supuestos de reestructuración.

19.3. **Acuerdo íntegro:** estos Términos, junto con la Política de Privacidad y demás avisos legales, constituyen el acuerdo completo entre las partes.

19.4. **Tolerancia:** la no exigencia por parte del Titular de algún derecho no supone renuncia al mismo.

## 20. Contacto

Para cualquier consulta relativa a estos Términos, puedes contactar con nosotros en:

- **Email:** legal@abako.xyz
- **Dirección:** Calle de la Tecnología 42, Madrid, España`;
